import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

// AI Experience Screens
import { AIOnboardingScreen } from '../screens/ai/AIOnboardingScreen';
import { AIJourneyFlowScreen } from '../screens/ai/AIJourneyFlowScreen';
import { AIAdaptiveLearningScreen } from '../screens/ai/AIAdaptiveLearningScreen';
import { AIPersonalizationScreen } from '../screens/ai/AIPersonalizationScreen';
import { AIAnalyticsScreen } from '../screens/ai/AIAnalyticsScreen';
import { AITestingHubScreen } from '../screens/ai/AITestingHubScreen';
import { SocialLearningAnalyticsScreen } from '../screens/SocialLearningAnalyticsScreen';
import { IntelligentSearchScreen } from '../screens/IntelligentSearchScreen';
import { AIAssessmentScreen } from '../screens/AIAssessmentScreen';
import AITutoringDashboardScreen from '../screens/ai/tutoring/AITutoringDashboardScreen';
import { PremiumAnalyticsDashboardScreen } from '../screens/PremiumAnalyticsDashboardScreen';
import { PeerMatchingScreen } from '../screens/ai/PeerMatchingScreen';
import { AdaptiveProgressScreen } from '../screens/AdaptiveProgressScreen';
import { ProgressInsightsScreen } from '../screens/ProgressInsightsScreen';
import { NotificationCenterScreen } from '../screens/NotificationCenterScreen';
import { MasteryVisualizationScreen } from '../screens/MasteryVisualizationScreen';
import { ResponsiveHeader } from '../components/layout';
import {
  getRouteTitle,
  shouldShowBackButton,
} from '../config/navigationConfig';

const Stack = createNativeStackNavigator();

export function AIExperienceNavigator() {
  return (
    <Stack.Navigator
      initialRouteName='index'
      screenOptions={{
        headerShown: true,
        header: (props: any) => <ResponsiveHeader {...props} />,
      }}
    >
      {/* Main AI experiments screen at root level - URL will be /aiExperience */}
      <Stack.Screen
        name='index'
        component={AITestingHubScreen}
        options={{
          title: getRouteTitle('aiExperience'),
          header: (props: any) => (
            <ResponsiveHeader {...props} forceShowBack={false} />
          ),
        }}
      />
      <Stack.Screen
        name='aiOnboarding'
        component={AIOnboardingScreen}
        options={{
          title: 'AI Onboarding',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiOnboarding')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='aiJourneyFlow'
        component={AIJourneyFlowScreen}
        options={{
          title: 'AI Journey Flow',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiJourneyFlow')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='aiAdaptiveLearning'
        component={AIAdaptiveLearningScreen}
        options={{
          title: 'Adaptive Learning',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiAdaptiveLearning')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='aiPersonalization'
        component={AIPersonalizationScreen}
        options={{
          title: 'Personalization',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiPersonalization')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='aiAnalytics'
        component={AIAnalyticsScreen}
        options={{
          title: 'AI Analytics',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiAnalytics')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='socialLearningAnalytics'
        component={SocialLearningAnalyticsScreen}
        options={{
          title: 'Social Learning',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('socialLearningAnalytics')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='intelligentSearch'
        component={IntelligentSearchScreen}
        options={{
          title: 'Intelligent Search',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('intelligentSearch')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='aiAssessment'
        component={AIAssessmentScreen}
        options={{
          title: 'AI Assessment',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiAssessment')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='aiTutoringDashboard'
        component={AITutoringDashboardScreen}
        options={{
          title: 'AI Tutoring',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('aiTutoringDashboard')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='premiumAnalyticsDashboard'
        component={PremiumAnalyticsDashboardScreen}
        options={{
          title: 'Premium Analytics',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('premiumAnalyticsDashboard')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='peerMatching'
        component={PeerMatchingScreen}
        options={{
          title: 'Peer Matching',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('peerMatching')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='adaptiveProgress'
        component={AdaptiveProgressScreen}
        options={{
          title: 'Adaptive Progress',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('adaptiveProgress')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='progressInsights'
        component={ProgressInsightsScreen}
        options={{
          title: 'Progress Insights',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('progressInsights')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='notificationCenter'
        component={NotificationCenterScreen}
        options={{
          title: 'Notifications',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('notificationCenter')}
            />
          ),
        }}
      />
      <Stack.Screen
        name='masteryVisualization'
        component={MasteryVisualizationScreen}
        options={{
          title: 'Mastery Visualization',
          header: (props: any) => (
            <ResponsiveHeader
              {...props}
              forceShowBack={shouldShowBackButton('masteryVisualization')}
            />
          ),
        }}
      />
    </Stack.Navigator>
  );
}
